import { Typography, TextField, Autocomplete, Stack, Button, Box, Divider, IconButton, Checkbox } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import PropTypes from "prop-types";
import { useState } from "react";
import UserList from "./UserList";
import { editTicketData, getTicketData } from "../../../../../../redux/slices/tickets";
import { postAddTicket } from "../../../../../../api/tickets";
import Iconify from "../../../../../../reusable-components/iconify";
import EditTicketSubject from "./EditTicketSubject";
import AwaitButton from "../../../../../../reusable-components/await-button/AwaitButton";

Subtickets.propTypes = {
  possibleUsers: PropTypes.array,
  setIsTicketInfoOpen: PropTypes.func,
  setIsAddingSubticket: PropTypes.func,
  isAddingSubticket: PropTypes.bool,
}
export default function Subtickets({ possibleUsers, setIsTicketInfoOpen, setIsAddingSubticket, isAddingSubticket }) {
  const dispatch = useDispatch();
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [isAddingNewUser, setIsAddingNewUser] = useState(false);
  const { user } = useSelector((state) => state.user);
  const { subtickets, mainTicket } = useSelector((state) => state.tickets.data.ticketInfoById[id] || {});
  
  const structureUser = (user) => ({id: user.email, value: user.userName || `${user.firstName} ${user.lastName}`});

  const [localTicketUsers, setLocalTicketUsers] = useState([structureUser(user)]);
  const [newSubticketSubject, setNewSubticketSubject] = useState(mainTicket?.extraInfo || '');

  const addNewUser = (newValue) => {
    const updatedUsers = newValue?.map((item) => {
      if (typeof item === 'string') {
        return { id: item, value: item };
      }
      return item;
    });
    setLocalTicketUsers(updatedUsers);
  };

  const removeUser = (id) => {
    const updatedUsers = localTicketUsers?.filter(user => user.id !== id);
    setLocalTicketUsers(updatedUsers);
    dispatch(editTicketData({
      Id: id,
      value: updatedUsers.map((user) => user.id),
      updateType: 'Users',
    }));
  }

  const addSubticket = async () => {
    postAddTicket({ message: '', parentTicketId: id, subject: newSubticketSubject })
      .then((response) => {
        dispatch(editTicketData({
          Id: response.data.id,
          value: localTicketUsers.map((user) => user.id),
          updateType: 'Users',
        }));
        dispatch(getTicketData(id)).then(() => {
          if (response.data.id) navigate(`/dashboard/tickets/${id}?subticket=${response.data.id}`);
        });
        setIsTicketInfoOpen(false);
      })
      .catch(() => enqueueSnackbar('Failed to add subticket', { variant: 'error' }))
      .finally(() => setIsAddingSubticket(false));
  };

  return <>
    <Typography color="info.main" variant="h6" sx={{mb: 1}}>
      Subtickets
    </Typography>
    {subtickets?.map(s =>
      <Box sx={{display: 'flex'}} key={s.id}>
        <Typography sx={{fontSize: '14px', mx: 1}}>
          {s.ticketName}
        </Typography>
        <EditTicketSubject ticketSubject={s.extraInfo || ''} ticketNumber={s.id} />
      </Box>
    )}
    {isAddingSubticket ? 
    <>
      <Typography color="secondary" sx={{ml: 1, my: 1}}>New Subticket</Typography>
      <Box sx={{ml: 1, display: 'flex'}}>
        <Divider orientation="vertical" sx={{mr: 1, borderWidth: '3px'}}/>
        <Box>
          <EditTicketSubject ticketSubject={newSubticketSubject} setExternalTicketSubject={setNewSubticketSubject} />
          <Typography color="text.disabled">
            Users
          </Typography>
          <UserList users={localTicketUsers} removeUser={(id) => removeUser(id)} />
          {isAddingNewUser && <Autocomplete
            size="small"
            multiple
            id="tags-outlined"
            options={possibleUsers || []}
            getOptionLabel={(option) => option?.value || option?.id}
            value={localTicketUsers}
            onChange={(_, newValue) => addNewUser(newValue)}
            freeSolo
            disableClearable
            renderTags={() => null}
            renderOption={(props, option) =>
              <li {...props} key={option.id || option} style={{ display: 'flex', alignItems: 'center' }}>
                <Checkbox
                  checked={localTicketUsers.some(u => u.id === option.id)}
                  style={{ marginRight: 8 }}
                    onChange={(e) => {
                      if (!e.target.checked) {
                        removeUser(localTicketUsers.find(u => u.email === option.id || u.id === option.id)?.id);
                      }
                      else {
                        addNewUser([...localTicketUsers, option]);
                      }
                    }
                  }
                />
                {option.value || option}
              </li>
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Add user"
                variant="standard"
                autoComplete="off"
              />
            )}
          />}
          <IconButton
            color="secondary"
            onClick={() => setIsAddingNewUser(true)}
          >
            <Iconify icon="eva:plus-fill" />
          </IconButton>
          <Stack direction="row" spacing={1} sx={{ mt: 1, justifyContent: 'flex-end', width: '100%' }}>
            <Button
              variant="text"
              color="secondary"
              size="small"
              sx={{textDecoration: 'underline'}}
              onClick={() => setIsAddingSubticket(false)}
            >Cancel</Button>
            <AwaitButton
              variant="outlined"
              color="secondary"
              size="small"
              onClick={addSubticket}
            >Save</AwaitButton>
          </Stack>
        </Box>
      </Box>
    </> : <Typography
      color="secondary"
      onClick={() => setIsAddingSubticket(true)}
      sx={{ textDecoration: 'underline', cursor: 'pointer', display: 'inline-block', ml: 1 }}
    >
      Add Subticket
    </Typography>}
  </>
}